import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Send, CheckCircle2, Loader2, Lock, Video } from "lucide-react";
import { Reveal, EASE } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/20";

const PIVA_OPTIONS = ["Sì", "No", "Sto valutando di aprirla"];

export default function Contatti() {
  const [form, setForm] = useState({
    nome_cognome: "",
    email: "",
    telefono: "",
    ha_piva: PIVA_OPTIONS[0],
    attivita: "",
    messaggio: "",
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (form.nome_cognome.trim().length < 2) e.nome_cognome = "Inserisci nome e cognome";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Inserisci un'email valida";
    if (form.telefono.trim().length < 6) e.telefono = "Inserisci un numero di telefono valido";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/contatti`, form);
      setSent(true);
      toast.success("Richiesta inviata! Un professionista ti ricontatterà al più presto.");
    } catch (err) {
      toast.error("Errore nell'invio. Riprova tra poco.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contatti"
      data-testid="section-contatti"
      className="relative overflow-hidden border-t border-white/5 bg-slate-950/40 py-24 lg:py-32"
    >
      <div className="pointer-events-none absolute left-[-5%] bottom-[5%] h-[420px] w-[420px] rounded-full bg-[#19D6E8]/10 blur-[130px]" />
      <div className="pointer-events-none absolute right-[-5%] top-[10%] h-[420px] w-[420px] rounded-full bg-[#1B6EF3]/15 blur-[130px]" />

      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-6 text-center" data-testid="cta-finale">
          <Reveal>
            <p className="text-base leading-relaxed text-slate-400 sm:text-lg">
              Non devi diventare esperto di burocrazia per lavorare bene. Con Human Factor hai uno spazio semplice per
              gestire la tua Partita IVA e un professionista al tuo fianco quando serve.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-display text-2xl font-extrabold leading-snug tracking-tight sm:text-3xl lg:text-4xl">
              Tu concentrati sul tuo lavoro.{" "}
              <span className="text-gradient">Alla gestione del regime forfettario pensiamo insieme.</span>
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="flex flex-col gap-3.5 sm:flex-row sm:items-center">
              <a
                href="#contatti-form"
                data-testid="cta-finale-btn-professionista"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("contatti-form")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-[#1B6EF3] to-[#19D6E8] px-7 py-4 font-display text-base font-bold text-white shadow-xl shadow-blue-500/30 transition-all duration-300 hover:scale-[1.03] hover:shadow-cyan-400/40"
              >
                <Video className="h-5 w-5" />
                Parla con un professionista
              </a>
              <a
                href="https://app.hfweb.it"
                data-testid="cta-finale-btn-accedi"
                className="inline-flex items-center justify-center gap-2.5 rounded-full border border-white/20 bg-white/5 px-7 py-4 font-display text-base font-semibold text-white backdrop-blur transition-colors hover:border-cyan-300/50"
              >
                <Lock className="h-4 w-4" />
                Accedi a Human Factor
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div id="contatti-form" className="mx-auto mt-16 max-w-3xl scroll-mt-28">
            <div className="glow-card relative rounded-3xl p-7 sm:p-10" data-testid="contatti-form-card">
              <AnimatePresence mode="wait">
                {sent ? (
                  <motion.div
                    key="success"
                    data-testid="contatti-success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: EASE }}
                    className="flex min-h-[420px] flex-col items-center justify-center gap-5 text-center"
                  >
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.15, type: "spring", stiffness: 200, damping: 12 }}
                      className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[#1B6EF3] to-[#19D6E8] text-white shadow-xl shadow-blue-500/30"
                    >
                      <CheckCircle2 className="h-9 w-9" />
                    </motion.span>
                    <h3 className="font-display text-2xl font-bold">Richiesta inviata!</h3>
                    <p className="max-w-sm text-sm leading-relaxed text-slate-400 sm:text-base">
                      Grazie{form.nome_cognome ? ` ${form.nome_cognome.split(" ")[0]}` : ""}: un nostro professionista
                      ti ricontatterà per rispondere alle tue domande e presentarti il servizio.
                    </p>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    data-testid="contatti-form"
                    onSubmit={onSubmit}
                    noValidate
                    exit={{ opacity: 0, scale: 0.97 }}
                    className="flex flex-col gap-4"
                  >
                    <div className="mb-1 text-center">
                      <h3 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                        Scopri se Human Factor <span className="text-gradient">è adatto a te</span>
                      </h3>
                      <p className="mt-2 text-sm text-slate-400 sm:text-base">
                        Raccontaci brevemente la tua situazione. Un nostro professionista ti ricontatterà per rispondere
                        alle tue domande e presentarti il servizio.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="f-nome" className="mb-1.5 block text-xs font-medium text-slate-400">
                          Nome e cognome *
                        </label>
                        <input
                          id="f-nome"
                          data-testid="form-input-name"
                          type="text"
                          placeholder="Mario Rossi"
                          value={form.nome_cognome}
                          onChange={(e) => set("nome_cognome", e.target.value)}
                          className={inputCls}
                        />
                        {errors.nome_cognome && <p className="mt-1 text-xs text-rose-400">{errors.nome_cognome}</p>}
                      </div>
                      <div>
                        <label htmlFor="f-email" className="mb-1.5 block text-xs font-medium text-slate-400">
                          Email *
                        </label>
                        <input
                          id="f-email"
                          data-testid="form-input-email"
                          type="email"
                          placeholder="mario@esempio.it"
                          value={form.email}
                          onChange={(e) => set("email", e.target.value)}
                          className={inputCls}
                        />
                        {errors.email && <p className="mt-1 text-xs text-rose-400">{errors.email}</p>}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="f-tel" className="mb-1.5 block text-xs font-medium text-slate-400">
                        Numero di telefono *
                      </label>
                      <input
                        id="f-tel"
                        data-testid="form-input-phone"
                        type="tel"
                        placeholder="+39 333 000 0000"
                        value={form.telefono}
                        onChange={(e) => set("telefono", e.target.value)}
                        className={inputCls}
                      />
                      {errors.telefono && <p className="mt-1 text-xs text-rose-400">{errors.telefono}</p>}
                    </div>

                    <div>
                      <span className="mb-2 block text-xs font-medium text-slate-400">Hai già una Partita IVA?</span>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" data-testid="form-radio-piva">
                        {PIVA_OPTIONS.map((opt) => (
                          <label
                            key={opt}
                            className={`cursor-pointer rounded-xl border px-4 py-3 text-center text-sm font-medium transition-all duration-200 ${
                              form.ha_piva === opt
                                ? "border-cyan-400/60 bg-gradient-to-r from-[#1B6EF3]/20 to-[#19D6E8]/15 text-white"
                                : "border-white/10 bg-white/5 text-slate-400 hover:border-cyan-400/30 hover:text-white"
                            }`}
                          >
                            <input
                              type="radio"
                              name="ha_piva"
                              value={opt}
                              checked={form.ha_piva === opt}
                              onChange={() => set("ha_piva", opt)}
                              data-testid={`form-radio-piva-${opt.toLowerCase().replace(/[^a-z]/g, "-")}`}
                              className="sr-only"
                            />
                            {opt}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="f-att" className="mb-1.5 block text-xs font-medium text-slate-400">
                        Di che cosa ti occupi?
                      </label>
                      <input
                        id="f-att"
                        data-testid="form-input-attivita"
                        type="text"
                        placeholder="Es. graphic designer, consulente, artigiano..."
                        value={form.attivita}
                        onChange={(e) => set("attivita", e.target.value)}
                        className={inputCls}
                      />
                    </div>

                    <div>
                      <label htmlFor="f-msg" className="mb-1.5 block text-xs font-medium text-slate-400">
                        Come possiamo aiutarti?
                      </label>
                      <textarea
                        id="f-msg"
                        data-testid="form-input-message"
                        rows="4"
                        placeholder="Raccontaci brevemente la tua situazione..."
                        value={form.messaggio}
                        onChange={(e) => set("messaggio", e.target.value)}
                        className={`${inputCls} resize-none`}
                      />
                    </div>

                    <motion.button
                      type="submit"
                      data-testid="form-submit-button"
                      disabled={sending}
                      whileHover={{ scale: sending ? 1 : 1.02 }}
                      whileTap={{ scale: sending ? 1 : 0.98 }}
                      className="mt-2 inline-flex items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-[#1B6EF3] to-[#19D6E8] px-7 py-4 font-display text-base font-bold text-white shadow-xl shadow-blue-500/30 transition-shadow hover:shadow-cyan-400/40 disabled:opacity-60"
                    >
                      {sending ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Invio in corso...
                        </>
                      ) : (
                        <>
                          Richiedi informazioni
                          <Send size={18} />
                        </>
                      )}
                    </motion.button>

                    <p className="text-center text-[11px] leading-relaxed text-slate-500">
                      Inviando la richiesta dichiari di aver letto l'informativa privacy e acconsenti al trattamento dei
                      dati necessario per essere ricontattato.
                    </p>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
